import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Route, Routes, useNavigate, Navigate } from "react-router-dom";
import ClipLoader from "react-spinners/ClipLoader";
import ActualConsumption from "./pages/MdbActualConsumptionReport";
import TheoreticalConsumption from "./pages/MdbTheoreticalConsumptionReport";
import ActualVersusTheoreticalConsumption from "./pages/MdbActualVersusTheoreticalConsumptionReport";
import GrossMarginAtProductLevel from "./pages/MdbGrossMarginAtProductLevel";
import CreateUser from "./pages/CreateUser";
import UserManagement from "./pages/ViewUsers";
import Login from "./pages/Login";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import TempPassword from "./pages/TempPassword";
import TopNav from "./components/TopNav";
import { createHandleProtectedAction } from "./authHelpers";
import VisualizationDashboard from "./pages/VisualizationDashboard";

import "./App.css";

const App = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const [authLoading, setAuthLoading] = useState(true);
  const [loading, setLoading] = useState({
    actual: false,
    theoretical: false,
    actualVersusTheoretical: false,
    grossMargin: false,
  });

  const logout = useCallback(() => {
    localStorage.removeItem("auth");
    localStorage.removeItem("actualDates");
    localStorage.removeItem("actualVersusTheoreticalDates");
    localStorage.removeItem("grossMarginDates");
    localStorage.removeItem("theoreticalDates");
    localStorage.removeItem("lastLoginDate");

    setIsLoggedIn(false);
    setUser(null);

    navigate("/login", { replace: true });
  }, [navigate]);

  // ---------- Auth bootstrap ----------
  useEffect(() => {
    const auth = localStorage.getItem("auth");
    const lastLoginDate = localStorage.getItem("lastLoginDate");

    if (!auth) {
      setAuthLoading(false);
      return;
    }
    const parsedAuth = JSON.parse(auth);

    setUser(parsedAuth.user);
    setIsLoggedIn(true);
    setAuthLoading(false);

    // Calculate time until next midnight
    const now = new Date();
    const midnight = new Date();
    midnight.setHours(24, 0, 0, 0);

    if (lastLoginDate !== now.toDateString()) {
      logout();
      return;
    }
    const timeout = midnight.getTime() - now.getTime();
    const timer = setTimeout(() => {
      logout();
    }, timeout);

    return () => clearTimeout(timer);
  }, [logout]);

  // ---------- ProtectedRoute ----------
  const ProtectedRoute = ({ children }) => {
    if (authLoading) {
      return (
        <div
          style={{ display: "flex", justifyContent: "center", marginTop: 120 }}
        >
          <ClipLoader size={40} />
        </div>
      );
    }

    if (!isLoggedIn) {
      return <Navigate to="/login" replace />;
    }

    if (user?.is_loggedin === false) {
      return <Navigate to="/reset-password" replace />;
    }

    return children;
  };

  // ---------- Guarded actions (e.g. must reset password) ----------
  const handleProtectedAction = createHandleProtectedAction({
    user,
    isLoggedIn,
    authLoading,
    navigate,
  });

  // ---------- Common date fetch ----------
  const fetchDates = async (consumptionType) => {
    if(!consumptionType) {
      return true;
    }
    let formattedConsumptionType =
      consumptionType.charAt(0).toLowerCase() + consumptionType.slice(1);
    formattedConsumptionType = formattedConsumptionType.split(" ").join("");

    const startTime = performance.now();

    try {
      setLoading((prev) => ({
        ...prev,
        [formattedConsumptionType]: true,
      }));

      if (!localStorage.getItem(`${formattedConsumptionType}Dates`)) {
        const response = await axios.get(
          `/api/fetch_consumption_calculated_dates`,
          { params: { consumptionType } }
        );

        if (response.data) {
          localStorage.setItem(
            `${formattedConsumptionType}Dates`,
            JSON.stringify(response.data)
          );
        }

        const endTime = performance.now();
        const durationSec = (endTime - startTime) / 1000;
        console.info(
          `[Timing] fetchDates(${consumptionType}) completed in ${durationSec.toFixed(
            3
          )} Seconds`
        );
      }

      return true;
    } catch (error) {
      alert("There was an error fetching the data!");
      return false;
    } finally {
      setLoading((prev) => ({
        ...prev,
        [formattedConsumptionType]: false,
      }));
    }
  };

  // ---------- Dashboard button config ----------
  const dashboardButtons = [
    {
      key: "actual",
      label: "Actual Consumption",
      consumptionType: "Actual",
      to: "/actual-consumption",
    },
    {
      key: "actualVersusTheoretical",
      label: "Actual Versus Theoretical",
      consumptionType: "Actual Versus Theoretical",
      to: "/actual-versus-theoretical-consumption",
    },
    {
      key: "theoretical",
      label: "Theoretical Consumption",
      consumptionType: "Theoretical",
      to: "/theoretical-consumption",
    },
    {
      key: "grossMargin",
      label: "Gross Margin",
      consumptionType: "Gross Margin",
      to: "/gross-margin-at-product-level",
    },
    // {
    //   key: "visualize",
    //   label: "Data visualization",
    //   consumptionType: 'Visualize',
    //   to: "/visualize",
    // },
  ];

  // ---------- Common click handler for dashboard buttons ----------
  const handleNavClick = (btn) => {
    if (btn.consumptionType !=='Visualize') {
      handleProtectedAction(async () => {
        const ok = await fetchDates(btn.consumptionType);
        if (ok) navigate(btn.to);
      });
    } else {
      navigate(btn.to);
    }
  };


  return (
    <>
      <Routes>
        {/* Public routes */}
        <Route
          path="/login"
          element={<Login setIsLoggedIn={setIsLoggedIn} setUser={setUser} />}
        />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route
          path="/reset-password"
          element={
            <>
              {isLoggedIn && (
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                  onProtectedAction={handleProtectedAction}
                />
              )}

              <ResetPassword setIsLoggedIn={setIsLoggedIn} setUser={setUser} />
            </>
          }
        />
        <Route path="/temp-password" element={<TempPassword />} />

        {/* Root */}
        <Route
          path="/"
          element={
            authLoading ? (
              <ClipLoader />
            ) : isLoggedIn ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <Navigate to="/login" replace />
            )
          }
        />

        {/* Dashboard */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                  onProtectedAction={handleProtectedAction}
                />

                <div className="button-container">
                  {dashboardButtons.map((btn) => (
                    <button
                      key={btn.key}
                      className="nav-button"
                      disabled={loading[btn.key]}
                      onClick={() => handleNavClick(btn)}
                    >
                      <span className="btn-content">
                        <span>{btn.label}</span>
                        {loading[btn.key] && (
                          <span className="btn-spinner">
                            <ClipLoader size={16} color="#fff" />
                          </span>
                        )}
                      </span>
                    </button>
                  ))}
                </div>
              </>
            </ProtectedRoute>
          }
        />

        {/* Report pages */}
        <Route
          path="/actual-consumption"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                />
                <ActualConsumption outlets={user?.branches} />
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/theoretical-consumption"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                />
                <TheoreticalConsumption outlets={user?.branches} />
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/actual-versus-theoretical-consumption"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                />
                <ActualVersusTheoreticalConsumption outlets={user?.branches} />
              </>
            </ProtectedRoute>
          }
        />

        <Route
          path="/gross-margin-at-product-level"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                />
                <GrossMarginAtProductLevel outlets={user?.branches} />
              </>
            </ProtectedRoute>
          }
        />
      {/*
        <Route
          path="/visualize"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                />
                <VisualizationDashboard />
              </>
            </ProtectedRoute>
          }
        />
        */}
        {/* Create User */}
        <Route
          path="/create-user"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                  onProtectedAction={handleProtectedAction}
                />
                <CreateUser
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                />
              </>
            </ProtectedRoute>
          }
        />

        {/* View Users */}
        <Route
          path="/view-users"
          element={
            <ProtectedRoute>
              <>
                <TopNav
                  user={user}
                  setIsLoggedIn={setIsLoggedIn}
                  setUser={setUser}
                  onProtectedAction={handleProtectedAction}
                />
                <UserManagement
                  auth_user={user}
                />
              </>
            </ProtectedRoute>
          }
        />
      </Routes>
    </>
  );
};

export default App;
